// Settings data access. Every endpoint this surface reads or writes, in one
// place, so the function-preservation table in the night's log can be checked
// against code rather than against memory.
//
// NO ENDPOINT IS INVENTED HERE (R4)
// =================================
// Every path below already exists on the backend and already serves the v1
// `/settings` page, the legacy `/offers` page, or an existing widget. Where §9
// names a section with no endpoint behind it — Models — this file deliberately
// has nothing for it rather than a plausible-looking guess.
//
// AMENDED BY SETTINGS → OFFERS PHASE A
// ====================================
// The original note said "nothing under `backend/` is edited by this session",
// which was true of the session that wrote it and is no longer true of this
// file. `docs/v2/pages/SETTINGS_OFFERS.md` Phase A did change the backend — it
// extended `OfferUpdate`, fixed the PATCH null-strip, and gave the offers
// export the columns and filters the v2 list shows. The rule the note was
// protecting still holds: no path below is a guess, and each one is a route
// that exists. Four entries are added for this page — `/offers/verticals`,
// `/offers/manual`, `/export/offers` and `/operators` — and all four predate it.
//
// FOUR STATES, AND "denied" IS THE ONE THAT MATTERS HERE
// ======================================================
// `brainApi.js` normalises into ok / disabled / error and that shape is
// followed rather than reinvented. One state is added: **denied**, for HTTP 403.
// Settings is the surface where founder-gating is densest — six of the reads
// below are `require_founder` — and a non-founder who reaches one must see a
// refusal, not a red error. Collapsing 403 into "error" would make the server
// correctly refusing look like the server being broken, which is exactly the
// impression R5 warns against.
//
// 404 stays "disabled" for the same reason it does in the Brain: a
// dark-launched capability is not a fault.
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AX = { withCredentials: true };

/** Normalise any settings read into { status, data, error, httpStatus }. */
function normalise(err) {
  const httpStatus = err && err.response && err.response.status;
  if (httpStatus === 404) {
    return { status: "disabled", data: null, httpStatus };
  }
  if (httpStatus === 403 || httpStatus === 401) {
    return { status: "denied", data: null, httpStatus };
  }
  return {
    status: "error",
    data: null,
    httpStatus,
    error: (err && err.message) || "Request failed",
  };
}

export async function get(path, params) {
  try {
    const res = await axios.get(`${API}${path}`, { ...AX, params });
    return { status: "ok", data: res.data, httpStatus: res.status };
  } catch (err) {
    return normalise(err);
  }
}

export async function put(path, body) {
  try {
    const res = await axios.put(`${API}${path}`, body, AX);
    return { status: "ok", data: res.data, httpStatus: res.status };
  } catch (err) {
    return normalise(err);
  }
}

export async function patch(path, body) {
  try {
    const res = await axios.patch(`${API}${path}`, body, AX);
    return { status: "ok", data: res.data, httpStatus: res.status };
  } catch (err) {
    return normalise(err);
  }
}

export async function post(path, body) {
  try {
    const res = await axios.post(`${API}${path}`, body, AX);
    return { status: "ok", data: res.data, httpStatus: res.status };
  } catch (err) {
    return normalise(err);
  }
}

export async function del(path) {
  try {
    const res = await axios.delete(`${API}${path}`, AX);
    return { status: "ok", data: res.data, httpStatus: res.status };
  } catch (err) {
    return normalise(err);
  }
}

// ── the endpoints, grouped by the section that consumes them ────────────────
//
// Each entry names the v1 origin it preserves. A reader checking the
// function-preservation claim should be able to start here.

export const ENDPOINTS = {
  // My Settings — preserved from SettingsPage
  notificationPreferences: "/operators/me/notification-preferences",
  onboardingReset: "/operators/me/onboarding/reset",

  // My Settings — PASSWORD-SET-UI-APPROVED. Founder-only on the server
  // (`require_founder`); the UI only ever sends the logged-in Founder's own
  // email, never an arbitrary email/operator_id the endpoint technically
  // accepts.
  setPassword: "/auth/set-password",

  // Offers — §3.10 sends the CRUD half of /offers and /campaigns/register here,
  // and `docs/v2/pages/SETTINGS_OFFERS.md` Phase A makes this the surface the
  // legacy `/offers` page can be switched off in favour of.
  offers: "/offers",
  offerManual: "/offers/manual",
  offerVerticals: "/offers/verticals",
  offersExport: "/export/offers",
  // Founder-only on the server (`require_founder`, with a written
  // justification). Read ONLY to populate the Assign picker, which is itself
  // Founder-only. A media buyer never calls it: the assigned buyer's display
  // name is resolved server-side onto the offer payload instead.
  operators: "/operators",
  campaigns: "/campaigns",
  businessUnits: "/business-units",
  networks: "/networks",

  // Offers — the pre-launch gate (#189). Read-only and advisory: it checks a
  // campaign name and a Meta "URL parameters" string for a campaign that does
  // not exist yet, and writes nothing anywhere. It lives on this surface
  // because the offer card is where the campaign-name stem is handed over,
  // which is the last thing Cipher does before the operator opens Meta Ads
  // Manager. Unlike `/validate-name` (#176) this endpoint has a caller —
  // `sections/Offers.jsx`. If that caller goes, remove the route with it.
  prelaunchCheck: "/prelaunch-check",

  // Connections — preserved from SettingsPage, plus existing status probes
  metaAccounts: "/meta/accounts",
  metaAccountsRefresh: "/meta/accounts/refresh",
  metaOAuthLogin: "/meta/oauth/login",
  telegramStatus: "/telegram/status",
  trelloStatus: "/trello/status",

  // Capabilities — the registry the Brain already exposes
  capabilities: "/brain/capabilities",
  capabilityCoverage: "/brain/capabilities/coverage",
  capabilityUnused: "/brain/capabilities/unused",

  // Infrastructure — IntegrationHealthWidget's source, plus the two cost
  // controls preserved from SettingsPage and A1's diagnostics home
  integrationsHealth: "/integrations/health",
  schedulersHealth: "/schedulers/health",
  buHealth: "/bu/health",
  apiUsageSummary: "/api-usage/summary",
  costAlertThreshold: "/cost-alert/threshold",
  circuitBreakerHardCap: "/circuit-breaker/hard-cap",
  circuitBreakerStatus: "/circuit-breaker/status",

  // People — founder-only on the server, and preserved from the v1
  // `/operators` page rather than invented here: that page is
  // `/operators/invite`'s only other caller and keeps working. #337,
  // 2026-10-29.
  operatorInvite: "/operators/invite",

  // System Configuration — preserved from SettingsPage
  auditLog: "/audit-log",
  auditActionTypes: "/audit-log/action-types",

  // Models — E9-INSTRUMENT's `model_calls` collection, read-only aggregate.
  // Was `NotBuilt` (no endpoint existed); L6 adds this one.
  modelCallsSummary: "/model-calls/summary",
};

/**
 * `?a=1&b=2` from an object, skipping empty values.
 *
 * Used by the two places that cannot go through axios's `params`: the CSV
 * export, which is an ordinary link the browser must follow, and
 * `PATCH /offers/{id}/assign`, which takes `operator_id` as a query parameter
 * rather than a body.
 */
export function query(params) {
  const q = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

/**
 * The CSV export URL.
 *
 * A plain link, not an XHR: the browser has to follow it for the download to
 * land, and the session cookie rides along the same way it does for the legacy
 * page's export link. The filters passed here are the ones the list is showing,
 * so the file matches the screen.
 */
export function offersExportUrl(params) {
  return `${API}${ENDPOINTS.offersExport}${query(params)}`;
}

/** Meta's OAuth start is a full-page redirect, not an XHR. */
export function metaOAuthUrl() {
  return `${API}${ENDPOINTS.metaOAuthLogin}`;
}
